<template>
  <v-card>
    <v-card-text>
      <v-row>
        <v-col cols="12" md="6">
          <v-select
            v-model="selectedSchedule"
            :items="schedules"
            item-title="title"
            :return-object="true"
            label="Select Schedule"
            :rules="[v => !!v || 'Schedule is required']"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-dialog
            v-model="dateDialog"
            width="auto"
          >
            <template v-slot:activator="{ props }">
              <v-text-field
                v-model="formattedDateTime"
                label="Select Date and Time"
                readonly
                v-bind="props"
                :rules="[v => !!v || 'Date and time is required']"
              >
                <template v-slot:prepend>
                  <v-icon>mdi-calendar</v-icon>
                </template>
              </v-text-field>
            </template>
            <v-card min-width="600">
              <v-card-text>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-date-picker
                      v-model="selectedDate"
                      :max="maxDate"
                      elevation="0"
                    ></v-date-picker>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-time-picker
                      v-model="selectedTime"
                      format="24hr"
                      elevation="0"
                    ></v-time-picker>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="error"
                  variant="text"
                  @click="cancelDateTime"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  variant="text"
                  @click="saveDateTimeSelection"
                  :disabled="!isDateTimeValid"
                >
                  OK
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :disabled="!isFormValid"
          @click="showConfirmDialog = true"
        >
          Restore
        </v-btn>
      </v-card-actions>
  
      <!-- Confirmation Dialog -->
      <v-dialog v-model="showConfirmDialog" persistent width="400">
        <v-card>
          <v-card-title class="text-h5">
            Confirm Restore
          </v-card-title>
          <v-card-text>
            Are you sure you would like to continue?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="error"
              variant="text"
              @click="showConfirmDialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              variant="text"
              @click="handleRestore"
            >
              OK
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Progress Dialog -->
      <v-dialog v-model="showProgressDialog" persistent width="300">
        <v-card>
          <v-card-text>
            <div class="d-flex flex-column align-center pa-4">
              <v-progress-circular
                indeterminate
                color="primary"
                :size="50"
                :width="5"
              ></v-progress-circular>
              <span class="mt-4 text-body-1">
                Restoring Schedule to Previous State
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
  
      <!-- Result Snackbar -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="3000"
      >
        {{ snackbar.text }}
        <template v-slot:actions>
          <v-btn
            color="white"
            variant="text"
            @click="snackbar.show = false"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-card>
  </template>
  
  <script setup>
  import { ref, computed } from 'vue';
  import { useAppStore } from '@/stores/app';
  import { useSettingsStore } from '@/stores/settings';
  import { useScheduleStore } from '@/stores/schedule';
  import { VTimePicker } from 'vuetify/labs/VTimePicker'

  const appStore = useAppStore();
  const settingsStore = useSettingsStore();
  const scheduleStore = useScheduleStore();
  
  // Component state
  const selectedSchedule = ref(null);
  const selectedDate = ref(null);
  const selectedTime = ref(null);
  const dateDialog = ref(false);
  const showConfirmDialog = ref(false);
  const showProgressDialog = ref(false);
  const snackbar = ref({
    show: false,
    text: '',
    color: 'success'
  });
  
  // Computed properties
  const schedules = computed(() => {
    const allSchedules = scheduleStore.getSchedules;

    return [...allSchedules].sort((a, b) => {
      return a.title.localeCompare(b.title, undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
  });
  const settings = computed(() => settingsStore.settings);

  const isDateTimeValid = computed(() => {
    return !!selectedDate.value && !!selectedTime.value;
  });
  
  const maxDate = computed(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  
  const formattedDateTime = computed(() => {
    if (selectedDate.value && selectedTime.value) {
      return `${selectedDate.value} ${selectedTime.value}`;
    }
    return '';
  });
  
  const isFormValid = computed(() => {
    return selectedSchedule.value && selectedDate.value && selectedTime.value;
  });
  
  // Methods
  const saveDateTimeSelection = () => {
    dateDialog.value = false;
  };
  
  const showMessage = (text, color = 'success') => {
    snackbar.value = {
      show: true,
      text,
      color
    };
  };
  
  const handleRestore = async () => {
  showConfirmDialog.value = false;
  showProgressDialog.value = true;

  try {
    // Parse time components from the time string
    const timeParts = selectedTime.value.split(':');
    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    
    // Create a new date object based on the selected date and set the time
    const dateObj = new Date(selectedDate.value);
    dateObj.setHours(hours, minutes, 0, 0);
    
    // Format to ISO string for the API
    const restoreDate = dateObj.toISOString();

    // Call the API (ScheduleRestoreDTO: restoreDate, title, message)
    await appStore.apiClient.post(
      `/api/${settings.value.projectCode}/schedules/${selectedSchedule.value.id}/restore`,
      {
        restoreDate,
        title: 'Schedule restore',
        message: `Restored schedule to ${restoreDate}`
      }
    );

    showMessage('Schedule restored successfully');
  } catch (error) {
    console.error('Error restoring schedule:', error);
    showMessage('Failed to restore schedule', 'error');
  } finally {
    showProgressDialog.value = false;
    // Reset form
    selectedSchedule.value = null;
    selectedDate.value = null;
    selectedTime.value = null;
  }
};
  </script>
  
  <style scoped>
  .v-card-text {
    padding-top: 20px;
  }
  </style>